import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { HealthLevel } from '../types';
import { healthLabel, ringStrokeClass } from '../utils';

interface UsageRingProps {
  label: string;
  sublabel?: string;
  percent: number;
  value: string;
  detail?: string;
  level: HealthLevel;
  size?: 'md' | 'lg';
  /** When true, ring fills completely for ok/error status (not usage %) */
  statusMode?: boolean;
}

const SIZE = { md: 96, lg: 120 } as const;
const STROKE = { md: 8, lg: 10 } as const;

const UsageRing: React.FC<UsageRingProps> = ({
  label,
  sublabel,
  percent,
  value,
  detail,
  level,
  size = 'md',
  statusMode = false,
}) => {
  const { colors } = useTheme();
  const dim = SIZE[size];
  const stroke = STROKE[size];
  const radius = (dim - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = statusMode
    ? level === 'ok'
      ? 100
      : level === 'error'
        ? 100
        : Math.min(100, Math.max(0, percent))
    : Math.min(100, Math.max(0, percent));
  const offset = circumference - (clamped / 100) * circumference;

  return (
    <div className={cn('flex flex-col items-center text-center', colors.text)}>
      <div className="relative" style={{ width: dim, height: dim }}>
        <svg width={dim} height={dim} className="-rotate-90" aria-hidden>
          <circle
            cx={dim / 2}
            cy={dim / 2}
            r={radius}
            fill="none"
            strokeWidth={stroke}
            className={cn('text-black/5 dark:text-white/10')}
            stroke="currentColor"
          />
          <circle
            cx={dim / 2}
            cy={dim / 2}
            r={radius}
            fill="none"
            strokeWidth={stroke}
            strokeLinecap="round"
            stroke="currentColor"
            className={cn('transition-all duration-500', ringStrokeClass(level))}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-2">
          {statusMode ? (
            <>
              <span className="text-sm font-bold">{value}</span>
              <span className={cn('text-[10px] uppercase tracking-wide font-semibold mt-0.5', colors.textMuted)}>
                {healthLabel(level)}
              </span>
            </>
          ) : (
            <>
              <span className="text-xl font-bold tabular-nums">{clamped}%</span>
              <span className={cn('text-[10px] uppercase tracking-wide font-semibold', colors.textMuted)}>
                {healthLabel(level)}
              </span>
            </>
          )}
        </div>
      </div>
      <p className={cn('mt-3 text-sm font-semibold', colors.text)}>{label}</p>
      {!statusMode && <p className={cn('text-lg font-bold tabular-nums mt-0.5', colors.text)}>{value}</p>}
      {sublabel && <p className={cn('text-xs mt-1 break-all max-w-[180px]', colors.textMuted)}>{sublabel}</p>}
      {detail && <p className={cn('text-xs mt-1', colors.textMuted)}>{detail}</p>}
    </div>
  );
};

export default UsageRing;
